import * as React from 'react';

import {AccessProvider} from '@sail/access';
import type {AccessConfig, Experiments, Flags, UsrFlag} from '@sail/access';
import {useAuthContext} from '../data-layer-client/AuthContext/AuthContext';
import {useConnectJsOptionsContext} from '../data-layer-client/ConnectJsOptionsContext/useConnectJsOptionsContext';
import {getStronglyTypedKeys} from '../utils/getStronglyTypedKeys';
import {getFlagOverrides} from '../utils/flags/flagOverrides';
import {getStronglyTypedEntries} from '../utils/getStronglyTypedEntries';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const EMPTY_FLAGS = {} as Flags;
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const EMPTY_EXPERIMENTS = {} as Experiments;

// TODO: this logic should be integrated inside ConnectSdkProvider, but it
// requires reading from multiple contexts that are below it in the tree
// (ConnectJsOptions and Auth).
export default function ConnectSdkProviderAccess({
  children,
}: {
  children: React.ReactNode;
}) {
  const connectJsOptions = useConnectJsOptionsContext();
  const {authInfo} = useAuthContext();

  const authFlags = authInfo?.flags ?? EMPTY_FLAGS;
  const metaFlags =
    connectJsOptions.values.metaOptions.flagOverrides ?? EMPTY_FLAGS;
  const experiments = authInfo?.treatments ?? EMPTY_EXPERIMENTS;

  const accessConfig: AccessConfig = React.useMemo(() => {
    const flags = {...authFlags};

    // In specific environments, we allow flag overrides

    // Meta overrides flags
    getStronglyTypedKeys(metaFlags).forEach((key) => {
      flags[key] = !!metaFlags[key];
    });

    // URL flags.
    const flagOverrides = getFlagOverrides();

    getStronglyTypedEntries(flagOverrides).forEach(([key, value]) => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      flags[key as UsrFlag] = value;
    });

    return {
      flags,
      experiments,
    };
  }, [authFlags, experiments, metaFlags]);

  return <AccessProvider config={accessConfig}>{children}</AccessProvider>;
}
